import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  Optional,
} from '@angular/core';

import { Table } from 'primeng/table';

/** Lee una medida CSS resuelta ("12px", "" o "auto") como número. */
function toPx(value: string): number {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Estira el cuerpo scrollable de un `p-table` hasta el borde inferior de la
 * ventana.
 *
 * Sustituye al `scrollHeight="calc(100vh - 440px)"` que traían estas pantallas.
 * Esa constante era una estimación a ojo de todo lo que hay por encima de la
 * tabla —topnav, cabeceras de `p-tabView`, párrafo de ayuda, barra de búsqueda,
 * cabecera de columnas— y de todo lo que hay por debajo —paginador—, así que
 * fallaba en cuanto cualquiera de esas piezas cambiaba de alto: el texto de
 * ayuda que pasa a ocupar dos renglones en vez de tres, una pantalla más ancha,
 * otro nivel de pestañas, un botón que aparece por permisos. Cuando la
 * estimación se pasaba de larga, la tabla se quedaba corta y sobraba espacio en
 * blanco al final de la página; que es justo lo que se veía.
 *
 * Aquí no se estima nada: se mide `getBoundingClientRect()` del contenedor
 * scrollable y se le resta lo que de verdad queda por debajo (paginador, pie de
 * tabla, `padding-bottom` de los contenedores). El resultado se reaplica con un
 * `ResizeObserver` sobre la cadena de ancestros, que es lo que cubre los casos
 * que la constante no podía cubrir: redimensionar la ventana, el reflujo del
 * texto de ayuda y volver a una pestaña que estaba oculta (`display: none`, con
 * lo que la medida anterior era 0).
 *
 * Contrato de la pantalla que lo usa: **ningún contenedor por encima de la
 * tabla debe fijar altura ni recortar** (`height: calc(100vh - …)`,
 * `overflow: hidden`). El alto lo decide el directive; un ancestro con altura
 * fija vuelve a recortar la tabla por su cuenta y deshace la medida.
 *
 * ```html
 * <p-table [scrollable]="true" appFillHeight> … </p-table>
 * ```
 */
@Directive({
  selector: 'p-table[appFillHeight]',
  standalone: true,
})
export class FillHeightDirective implements AfterViewInit, OnDestroy {
  /** Aire que se deja entre el final de la tabla y el borde de la ventana. */
  @Input() fillHeightGap = 12;

  /** Suelo del área scrollable: por debajo de esto la tabla ya no encoge. */
  @Input() fillHeightMin = 180;

  private wrapper: HTMLElement | null = null;
  private observer?: ResizeObserver;
  private frame = 0;
  /** Último alto aplicado, para no reescribir el DOM en cada notificación. */
  private applied = -1;

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone,
    @Optional() private table: Table | null,
  ) {}

  ngAfterViewInit(): void {
    this.wrapper = this.el.nativeElement.querySelector<HTMLElement>('.p-datatable-wrapper');
    if (!this.wrapper) {
      return;
    }
    // Fuera de la zona: el observer dispara en cada reflujo y no cambia estado
    // de Angular, sólo un estilo. Entrar a la zona aquí sería un tick de
    // detección de cambios por frame mientras se arrastra el borde de la ventana.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('resize', this.schedule, { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(this.schedule);
        for (const ancestor of this.ancestors()) {
          this.observer.observe(ancestor);
        }
      }
      this.schedule();
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.schedule);
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  /**
   * Agrupa en un frame las notificaciones del observer. Medir dentro del propio
   * callback dispararía el aviso de "ResizeObserver loop" del navegador, porque
   * la escritura del alto provoca otro reflujo.
   */
  private schedule = (): void => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(this.measure);
  };

  private measure = (): void => {
    const wrapper = this.wrapper;
    // Sin cajas: la tabla está en una pestaña oculta (`display: none`) y toda
    // medida saldría 0. Al volver a mostrarse, el observer avisa.
    if (!wrapper || !wrapper.isConnected || !wrapper.getClientRects().length) {
      return;
    }

    const viewport = document.documentElement.clientHeight;
    const top = wrapper.getBoundingClientRect().top;
    const height = Math.max(
      this.fillHeightMin,
      Math.floor(viewport - top - this.spaceBelow(wrapper) - this.fillHeightGap),
    );
    if (Math.abs(height - this.applied) < 1) {
      return;
    }
    this.applied = height;

    const value = `${height}px`;
    // El estilo directo evita esperar a la detección de cambios; la asignación
    // al input deja coherente el `[ngStyle]` interno de la tabla, que es quien
    // reescribiría `max-height` en el siguiente ciclo.
    wrapper.style.maxHeight = value;
    if (this.table) {
      this.table.scrollHeight = value;
    }
  };

  /**
   * Alto de todo lo que queda por debajo del área scrollable hasta el final de
   * la página: el paginador y el pie de la propia tabla, y en cada nivel
   * superior los hermanos posteriores y el `padding-bottom` del contenedor.
   */
  private spaceBelow(from: HTMLElement): number {
    let total = 0;
    for (let node: HTMLElement = from; node.parentElement && node !== document.body;) {
      const parent = node.parentElement;
      for (let next = node.nextElementSibling; next; next = next.nextElementSibling) {
        total += this.outerHeight(next as HTMLElement);
      }
      const style = getComputedStyle(parent);
      total += toPx(style.paddingBottom) + toPx(style.borderBottomWidth);
      node = parent;
    }
    return total;
  }

  /** Alto ocupado por un hermano. Lo que no ocupa sitio en el flujo cuenta 0. */
  private outerHeight(el: HTMLElement): number {
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.position === 'absolute' || style.position === 'fixed') {
      return 0;
    }
    return el.getBoundingClientRect().height + toPx(style.marginTop) + toPx(style.marginBottom);
  }

  /** Cadena de ancestros del área scrollable, hasta `body` incluido. */
  private ancestors(): HTMLElement[] {
    const chain: HTMLElement[] = [];
    for (let node = this.wrapper?.parentElement; node; node = node.parentElement) {
      chain.push(node);
      if (node === document.body) {
        break;
      }
    }
    return chain;
  }
}
